/**
 * Anchor-based layout system for dynamic canvas heights.
 *
 * All vertical UI positions are computed relative to canvas edges so the game
 * adapts to any mobile screen height (480-900px) while keeping desktop (600px)
 * rendering identical to the pre-dynamic hardcoded layout.
 *
 * Strategy:
 * - HUD is always top-anchored (fixed from Y=0).
 * - Bottom controls are bottom-anchored when there is enough room.
 * - Board sits flush against the control zone (biased downward) to match
 *   the legacy layout at 600px (~130 boardOriginY).
 * - On very short screens (< ~543px), the board still starts below the HUD
 *   and the controls shift to sit directly below the board, compressing gaps.
 */

export interface LayoutMetrics {
  canvasHeight: number;
  // Top-anchored (relative to Y=0)
  headerTopBarY: number;
  hudPanelY: number;
  hudPanelBottom: number;
  // Board (centered between hud bottom and controls top)
  boardOriginY: number;
  boardBottom: number;
  // Bottom-anchored (relative to canvasHeight)
  grooveMeterY: number;
  spinsIndicatorY: number;
  controlRowY: number;
  powerupRowY: number;
  characterBaseY: number;
  // Tutorial panel
  tutorialPanelY: number;
}

import { isMobileViewport } from "./platform";
import { CANVAS_TUNING } from "./tuning";

const BOARD_PIXEL_SIZE_DESKTOP = CANVAS_TUNING.boardPixelSize; // 320
const BOARD_PIXEL_SIZE_MOBILE = CANVAS_TUNING.width; // 360 (full canvas width)
const GAMEPLAY_CLUSTER_DROP_TARGET_PX = 15;
const CONTROL_ROW_HEIGHT_PX = 58;
const MIN_CONTROL_TO_POWERUP_GAP_PX = 2;

// Desktop: spins indicator removed → groove meter hugs grid, controls hug groove
const DESKTOP_BOARD_TO_GROOVE_GAP_PX = 4;
const DESKTOP_GROOVE_TO_CONTROL_GAP_PX = 4;

/**
 * Pure function that computes all vertical layout positions for a given
 * canvas height. Designed so that canvasHeight=600 reproduces the legacy
 * hardcoded positions (boardOriginY ~130).
 */
export function computeLayout(canvasHeight: number): LayoutMetrics {
  const BOARD_PIXEL_SIZE = isMobileViewport() ? BOARD_PIXEL_SIZE_MOBILE : BOARD_PIXEL_SIZE_DESKTOP;
  // ── 1. Top zone (top-anchored, same as current hardcoded values) ──
  const headerTopBarY = 9;
  const hudPanelY = 52;
  const panelH = 14;
  const progressGap = 3;
  const progressStripH = 6;
  const hudPanelBottom = hudPanelY + panelH + progressGap + progressStripH; // 75
  const topZoneEnd = hudPanelBottom + 8; // 83

  // ── 2. Bottom zone (bottom-anchored, ideal positions) ──
  const iconSize = 40;
  const idealPowerupRowY = canvasHeight - iconSize - 10; // canvasHeight - 50
  const portraitSize = 160;
  const characterBaseY = canvasHeight - portraitSize;

  const vinylSize = 58;
  const grooveH = 30;
  const ctrlGap = 14; // gap between the control row and the powerup row

  // Work backwards from powerup row
  const mobile = isMobileViewport();
  const idealControlRowY = idealPowerupRowY - ctrlGap - vinylSize;

  // On mobile the horizontal doom bar (26px + 6px gap) sits below the board,
  // so groove meter must start below it. Desktop uses tight gap (spins indicator removed).
  const DOOM_BAR_MOBILE_H = 26 + 6 + 2; // barH + gap above + gap below (tight)
  const boardToGrooveGap = mobile ? DOOM_BAR_MOBILE_H : DESKTOP_BOARD_TO_GROOVE_GAP_PX;
  const grooveToControlGap = mobile
    ? 4 // tight gap: controls hug groove meter
    : DESKTOP_GROOVE_TO_CONTROL_GAP_PX;

  const idealSpinsIndicatorY = idealControlRowY - grooveToControlGap - grooveH;
  const idealGrooveMeterY = idealSpinsIndicatorY;

  // ── 3. Board positioning ──
  // Place the board so its bottom edge is `boardToGrooveGap` above the groove meter.
  const idealBoardOriginY = idealGrooveMeterY - boardToGrooveGap - BOARD_PIXEL_SIZE;

  let boardOriginY: number;
  let grooveMeterY: number;
  let spinsIndicatorY: number;
  let controlRowY: number;
  let powerupRowY: number;

  if (idealBoardOriginY >= topZoneEnd) {
    // Enough room: center board+groove+controls in the space between HUD and powerups.
    // Character + powerups stay bottom-anchored, HUD stays top-anchored.
    powerupRowY = idealPowerupRowY;

    // Use the legacy mid-block height for centering so the board position stays stable.
    // The visual gaps below the board are tighter (spins indicator removed on desktop),
    // but centering is based on the original block footprint to avoid pushing the board down.
    const LEGACY_BELOW_BOARD = 12 + 30 + 4 + 18 + 6 + vinylSize; // original boardToGroove + groove + spins + controls
    const midBlockH = BOARD_PIXEL_SIZE + LEGACY_BELOW_BOARD;
    const midZoneTop = topZoneEnd;
    const midZoneBottom = idealPowerupRowY - ctrlGap; // leave gap above powerups
    const midZoneH = midZoneBottom - midZoneTop;
    // Cap centering offset so the board stays close to the HUD on tall phones
    // (600px → 22px offset unchanged; 780px → capped at 30 instead of 112)
    const rawMidOffset = Math.floor((midZoneH - midBlockH) / 2);
    const midOffset = Math.min(rawMidOffset, 30);

    boardOriginY = midZoneTop + Math.max(0, midOffset);
    // Groove meter + controls hug the board with tight gaps
    grooveMeterY = boardOriginY + BOARD_PIXEL_SIZE + boardToGrooveGap;
    spinsIndicatorY = grooveMeterY; // groove meter now uses spinsIndicatorY slot
    controlRowY = spinsIndicatorY + grooveH + grooveToControlGap;
  } else {
    // Short screen: board starts right below HUD, controls flow below board.
    // Compress gaps to fit everything.
    boardOriginY = topZoneEnd;
    const boardBottom = boardOriginY + BOARD_PIXEL_SIZE;
    const remainingBelow = canvasHeight - boardBottom;

    // Minimum control zone
    const minGap = 4;
    const controlToPowerupMinGap = Math.max(MIN_CONTROL_TO_POWERUP_GAP_PX, minGap);
    const controlZoneNeeded =
      grooveH
      + grooveToControlGap
      + vinylSize
      + controlToPowerupMinGap
      + iconSize
      + minGap;

    if (remainingBelow >= controlZoneNeeded + boardToGrooveGap) {
      // Enough room for controls with reduced gaps.
      grooveMeterY = boardBottom + boardToGrooveGap;
      spinsIndicatorY = grooveMeterY;
      const afterGroove = canvasHeight - spinsIndicatorY - grooveH;
      const gapSlots = 2;
      const usedByElements = grooveToControlGap + vinylSize + iconSize;
      const gapEach = Math.max(minGap, Math.floor((afterGroove - usedByElements) / gapSlots));
      controlRowY = spinsIndicatorY + grooveH + grooveToControlGap;
      powerupRowY = controlRowY + vinylSize + gapEach;
    } else {
      // Very tight: reduce board-to-groove gap and compress everything.
      const totalElements = grooveH + vinylSize + iconSize;
      const totalFixedGaps = grooveToControlGap;
      const totalGaps = Math.max(0, remainingBelow - totalElements - totalFixedGaps);
      const gapEach = Math.floor(totalGaps / 3); // board-groove, ctrl-pwr, pwr-bottom
      const boardToGrooveGapTight = Math.max(1, gapEach);
      const controlToPowerupGapTight = Math.max(MIN_CONTROL_TO_POWERUP_GAP_PX, gapEach);
      grooveMeterY = boardBottom + boardToGrooveGapTight;
      spinsIndicatorY = grooveMeterY;
      controlRowY = spinsIndicatorY + grooveH + grooveToControlGap;
      powerupRowY = controlRowY + vinylSize + controlToPowerupGapTight;
    }
  }

  // ── 4. Safe area enforcement ──
  if (boardOriginY < topZoneEnd) {
    boardOriginY = topZoneEnd;
  }

  const maxSafeDrop = Math.max(0, powerupRowY - (controlRowY + CONTROL_ROW_HEIGHT_PX + MIN_CONTROL_TO_POWERUP_GAP_PX));
  const clusterDrop = Math.min(GAMEPLAY_CLUSTER_DROP_TARGET_PX, maxSafeDrop);
  boardOriginY += clusterDrop;
  grooveMeterY += clusterDrop;
  spinsIndicatorY += clusterDrop;
  controlRowY += clusterDrop;

  const boardBottom = boardOriginY + BOARD_PIXEL_SIZE;

  // ── 5. Tutorial panel ──
  const tutorialPanelY = boardBottom + 8;

  return {
    canvasHeight,
    headerTopBarY,
    hudPanelY,
    hudPanelBottom,
    boardOriginY,
    boardBottom,
    grooveMeterY,
    spinsIndicatorY,
    controlRowY,
    powerupRowY,
    characterBaseY,
    tutorialPanelY,
  };
}

// ── Memoized helper ──

let cachedHeight = -1;
let cachedLayout: LayoutMetrics | null = null;

/**
 * Returns a memoized LayoutMetrics for the given canvas height.
 * Re-computes only when the height changes.
 */
export function getLayout(canvasHeight: number): LayoutMetrics {
  if (canvasHeight !== cachedHeight || cachedLayout === null) {
    cachedLayout = computeLayout(canvasHeight);
    cachedHeight = canvasHeight;
  }
  return cachedLayout;
}
